import { createContext, useState, useMemo, useEffect } from "react";
import { createTheme } from "@mui/material/styles";

const COLOR_MODE_KEY = "dashboardColorMode";

export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#f0f0f0",
          200: "#d9d9d9",
          300: "#b5b5b5",
          400: "#8a8a8a",
          500: "#5c5c5c",
          600: "#3a3a3a",
          700: "#2a2a2a",
          800: "#1c1c1c",
          900: "#111111",
        },
        primary: {
          100: "#f0f0f0",
          200: "#d9d9d9",
          300: "#8a8a8a",
          400: "#2a2a2a",
          500: "#1c1c1c",
          600: "#161616",
          700: "#111111",
          800: "#0c0c0c",
          900: "#080808",
        },
        greenAccent: {
          100: "#e9f8e4",
          200: "#c5edb6",
          300: "#93dd78",
          400: "#6dce4a",
          500: "#49BE25",
          600: "#3a981e",
          700: "#2b7216",
          800: "#1c4c0f",
          900: "#0e2607",
        },
        redAccent: {
          100: "#fde8e9",
          200: "#f9b6b8",
          300: "#f57a7e",
          400: "#f0444a",
          500: "#ec1d23",
          600: "#c9181d",
          700: "#9d1217",
          800: "#710d10",
          900: "#45080a",
        },
        blueAccent: {
          100: "#f0f0f0",
          200: "#d9d9d9",
          300: "#b5b5b5",
          400: "#8a8a8a",
          500: "#353535",
          600: "#2a2a2a",
          700: "#1c1c1c",
          800: "#161616",
          900: "#111111",
        },
      }
    : {
        grey: {
          100: "#1c1c1c",
          200: "#2a2a2a",
          300: "#353535",
          400: "#5c5c5c",
          500: "#8a8a8a",
          600: "#b5b5b5",
          700: "#d9d9d9",
          800: "#f0f0f0",
          900: "#f7f7f7",
        },
        primary: {
          100: "#1c1c1c",
          200: "#2a2a2a",
          300: "#353535",
          400: "#ffffff",
          500: "#1c1c1c",
          600: "#353535",
          700: "#8a8a8a",
          800: "#d9d9d9",
          900: "#f0f0f0",
        },
        greenAccent: {
          100: "#0e2607",
          200: "#1c4c0f",
          300: "#2b7216",
          400: "#3a981e",
          500: "#49BE25",
          600: "#6dce4a",
          700: "#93dd78",
          800: "#c5edb6",
          900: "#e9f8e4",
        },
        redAccent: {
          100: "#45080a",
          200: "#710d10",
          300: "#9d1217",
          400: "#c9181d",
          500: "#ec1d23",
          600: "#f0444a",
          700: "#f57a7e",
          800: "#f9b6b8",
          900: "#fde8e9",
        },
        blueAccent: {
          100: "#111111",
          200: "#161616",
          300: "#1c1c1c",
          400: "#2a2a2a",
          500: "#353535",
          600: "#8a8a8a",
          700: "#b5b5b5",
          800: "#d9d9d9",
          900: "#f0f0f0",
        },
      }),
});

const fontStack = '"Inter", "Source Sans Pro", sans-serif';

export const themeSettings = (mode) => {
  const colors = tokens(mode);
  const isDark = mode === "dark";
  const brand = "#ec1d23";
  const paper = isDark ? "#2a2a2a" : "#ffffff";
  const canvas = isDark ? "#1c1c1c" : "#f0f0f0";
  const divider = isDark ? "rgba(255,255,255,0.08)" : "rgba(28,28,28,0.10)";
  const textPrimary = isDark ? "#f0f0f0" : "#353535";

  return {
    palette: {
      mode,
      primary: {
        main: brand,
        contrastText: "#ffffff",
      },
      secondary: {
        main: brand,
        contrastText: "#ffffff",
      },
      success: {
        main: "#49BE25",
        contrastText: "#ffffff",
      },
      error: {
        main: "#E9313C",
        contrastText: "#ffffff",
      },
      info: {
        main: isDark ? "#f0f0f0" : "#353535",
        contrastText: isDark ? "#1c1c1c" : "#ffffff",
      },
      warning: {
        main: "#ffac47",
        contrastText: "#1c1c1c",
      },
      neutral: {
        dark: colors.grey[700],
        main: colors.grey[500],
        light: colors.grey[100],
      },
      background: {
        default: canvas,
        paper,
      },
      divider,
      text: {
        primary: textPrimary,
        secondary: isDark ? "#9aa0aa" : "#5c5c5c",
      },
    },
    shape: {
      borderRadius: 10,
    },
    typography: {
      fontFamily: fontStack,
      fontSize: 13,
      h1: { fontFamily: fontStack, fontSize: 36, fontWeight: 650 },
      h2: { fontFamily: fontStack, fontSize: 28, fontWeight: 650 },
      h3: { fontFamily: fontStack, fontSize: 22, fontWeight: 600 },
      h4: { fontFamily: fontStack, fontSize: 18, fontWeight: 600 },
      h5: { fontFamily: fontStack, fontSize: 15, fontWeight: 550 },
      h6: { fontFamily: fontStack, fontSize: 13, fontWeight: 600, letterSpacing: 0.3 },
      button: {
        fontFamily: fontStack,
        textTransform: "none",
        fontWeight: 600,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: canvas,
            color: textPrimary,
            "--search-bg": isDark ? "#2a2a2a" : "#ffffff",
            "--search-border": divider,
            "--search-color": textPrimary,
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: "transparent" },
        styleOverrides: {
          root: {
            backgroundColor: `${paper} !important`,
            color: `${textPrimary} !important`,
            border: `1px solid ${divider}`,
            borderRadius: 12,
            boxShadow: "none",
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: "64px !important",
            gap: 8,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: "none",
            fontWeight: 600,
            letterSpacing: 0,
            paddingLeft: 16,
            paddingRight: 16,
            minHeight: 40,
            minWidth: "max-content",
            flexShrink: 0,
            whiteSpace: "nowrap",
            lineHeight: 1.3,
          },
          contained: {
            boxShadow: "none",
          },
          containedSuccess: {
            color: "#fff",
          },
          containedSecondary: {
            color: "#fff",
          },
          containedPrimary: {
            color: "#fff",
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: `1px solid ${divider}`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: paper,
            border: `1px solid ${divider}`,
            borderRadius: 12,
            boxShadow: "none",
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            width: "100%",
            tableLayout: "auto",
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            "& .MuiTableRow-root:hover > .MuiTableCell-root": {
              backgroundColor: isDark
                ? "rgba(255,255,255,0.10)"
                : "rgba(0,0,0,0.06)",
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: divider,
          },
          head: {
            fontWeight: 650,
            backgroundColor: isDark ? "#242424" : "#f7f7f7",
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${divider}`,
            backgroundColor: paper,
            overflowX: "auto",
            maxWidth: "100%",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: "filled" },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: isDark ? "#b5b5b5" : "#5c5c5c",
            "&.Mui-focused": {
              color: `${brand} !important`,
            },
            "&.Mui-error": {
              color: colors.redAccent[400],
            },
            "& .MuiFormLabel-asterisk": {
              color: colors.redAccent[500],
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: isDark ? "#b5b5b5" : "#5c5c5c",
            "&.Mui-focused": {
              color: `${brand} !important`,
            },
            "&.Mui-error": {
              color: colors.redAccent[400],
            },
            "& .MuiFormLabel-asterisk": {
              color: colors.redAccent[500],
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            "&.Mui-checked": {
              color: brand,
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(28,28,28,0.04)",
            "&:before, &:after": { display: "none" },
            "&:hover": {
              backgroundColor: isDark ? "rgba(255,255,255,0.07)" : "rgba(28,28,28,0.06)",
            },
            "&.Mui-focused": {
              backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "#fff",
              boxShadow: `inset 0 0 0 1px ${brand}`,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            border: `1px solid ${divider}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: "none",
            backgroundColor: paper,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            border: `1px solid ${divider}`,
          },
        },
      },
    },
  };
};

export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = (direction = "ltr") => {
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem(COLOR_MODE_KEY) === "light" ? "light" : "dark";
    } catch {
      return "dark";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLOR_MODE_KEY, mode);
    } catch {
      // ignore storage failures
    }
  }, [mode]);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prev) => (prev === "light" ? "dark" : "light"));
      },
    }),
    []
  );

  const theme = useMemo(
    () => createTheme({ ...themeSettings(mode), direction }),
    [mode, direction]
  );
  return { theme, colorMode };
};
